export const USER_TYPES = {
  admin: 'admin',
  user: 'user',
  owner: 'owner',
} as const

export const LOCATION_STATUSES = {
  accepted: 'accepted',
  inConstruction: 'in_construction',
  opened: 'opened',
  potential: 'potential',
  rejected: 'rejected',
} as const

export const featureAccessIds = {
  kanban: 'kanban',
  excelReport: 'excelReport',
  areasAnalysis: 'areasAnalysis',
  medicalInfo: 'medicalInfo',
} as const

export const trafficVersions = {
  v1: 'v1',
  v2: 'v2',
  v3: 'v3',
} as const

export type FeatureAccessId = (typeof featureAccessIds)[keyof typeof featureAccessIds]

export const dataAttributes = {
  chooseLocation: 'chooseLocation',
  analysesAndReports: {
    reportsButton: 'chooseLocationReportsButton',
  },
  analysePage: {
    header: {
      downloadButton: 'analysePageHeaderDownloadButton',
      saveAnalyseButton: 'analysePageHeaderSaveAnalyseButton',
      actionsWrapper: 'analysePageHeaderActionsWrapper',
      selectReportType: 'analysePageHeaderSelectReportType',
      selectFolder: 'analysePageHeaderSelectFolder',
    },
    sidebar: {
      sidebarList: 'analysePageSidebarList',
      dropdownCategoryTile: {
        traffic: 'analysePageSidebarDropdownCategoryTileTraffic',
        population: 'analysePageSidebarDropdownCategoryTilePopulation',
        market: 'analysePageSidebarDropdownCategoryTileMarket',
        access_logistics: 'analysePageSidebarDropdownCategoryTileAccessLogistics',
        potential: 'analysePageSidebarDropdownCategoryTilePotential',
        visualization: 'analysePageSidebarDropdownCategoryTileVisualization',
        well_fit: 'analysePageSidebarDropdownCategoryTileWellFit',
        big_ben: 'analysePageSidebarDropdownCategoryTileBigBen',
        mcd: 'analysePageSidebarDropdownCategoryTileMcd',
        anw: 'analysePageSidebarDropdownCategoryTileAnw',
        dol_sap: 'analysePageSidebarDropdownCategoryTileDolSap',
        snv: 'analysePageSidebarDropdownCategoryTileSnv',
        demo: 'analysePageSidebarDropdownCategoryTileDemo',
      },
      dropdownTile: {
        // inhabitants_and_visitors
        people: 'analysePageSidebarDropdownTilePeople',
        location_type: 'analysePageSidebarDropdownTileLocationType',
        overlapping_ranges: 'analysePageSidebarDropdownTileOverlappingRanges',
        // population
        demography: 'analysePageSidebarDropdownTileDemography',
        spending: 'analysePageSidebarDropdownTileSpending',
        purchasing_power: 'analysePageSidebarDropdownTilePurchasingPower',
        // traffic
        traffic_visualisation: 'analysePageSidebarDropdownTileTrafficVisualisation',
        visiting_hours: 'analysePageSidebarDropdownTileVisitingHours',
        customer_origin: 'analysePageSidebarDropdownTileCustomerOrigin',
        // market
        pois: 'analysePageSidebarDropdownTilePois',
        cannibalization: 'analysePageSidebarDropdownTileCannibalization',
        // surroundings
        buildings: 'analysePageSidebarDropdownTileBuildings',
        investments_new: 'analysePageSidebarDropdownTileInvestmentsNew',
        employees_in_range: 'analysePageSidebarDropdownTileEmployeesInRange',
        municipality_population: 'analysePageSidebarDropdownTileMunicipalityPopulation',
        // access_logistics
        transport: 'analysePageSidebarDropdownTileTransport',
        access_range: 'analysePageSidebarDropdownTileAccessRange',
        // potential
        potential_model: 'analysePageSidebarDropdownTilePotentialModel',
        gravity_model: 'analysePageSidebarDropdownTileGravityModel',
        potential_within_chain: 'analysePageSidebarDropdownTilePotentialWithinChain',
        // visualization
        cities_population: 'analysePageSidebarDropdownTileCitiesPopulation',
        population_heatmap: 'analysePageSidebarDropdownTilePopulationHeatmap',
        traffic_visualisation_map: 'analysePageSidebarDropdownTileTrafficVisualisationMap',
        land_use_plan: 'analysePageSidebarDropdownTileLandUsePlan',
        satellite: 'analysePageSidebarDropdownTileSatellite',
        // well_fit
        well_fit_potential_model: 'analysePageSidebarDropdownTileWellFitPotentialModel',
        well_fit_competition: 'analysePageSidebarDropdownTileWellFitCompetition',
        // big_ben
        population_big_ben: 'analysePageSidebarDropdownTilePopulationBigBen',
        traffic_big_ben: 'analysePageSidebarDropdownTileTrafficBigBen',
        pois_big_ben: 'analysePageSidebarDropdownTilePoisBigBen',
        segmentation_big_ben: 'analysePageSidebarDropdownTileSegmentationBigBen',
        look_alike_big_ben: 'analysePageSidebarDropdownTileLookAlikeBigBen',
        scoring_model_big_ben: 'analysePageSidebarDropdownTileScoringModelBigBen',
        traffic_visualisation_big_ben: 'analysePageSidebarDropdownTileTrafficVisualisationBigBen',
        old_traffic_visualisation_big_ben: 'analysePageSidebarDropdownTileOldTrafficVisualisationBigBen',
        // mcd
        mcd_potential_model: 'analysePageSidebarDropdownTileMcdPotentialModel',
        mcd_store_chain_impact: 'analysePageSidebarDropdownTileMcdStoreChainImpact',
        mcd_competition_traffic: 'analysePageSidebarDropdownTileMcdCompetitionTraffic',
        // anw
        anw_competition: 'analysePageSidebarDropdownTileAnwCompetition',
        anw_potential_model: 'analysePageSidebarDropdownTileAnwPotentialModel',
        // dol_sap
        dol_sap_potential_within_chain: 'analysePageSidebarDropdownTileDolSapPotentialWithinChain',
        // snv
        snv_potential_model: 'analysePageSidebarDropdownTileSnvPotentialModel',
        // demo
        demo_scoring_prediction: 'analysePageSidebarDropdownTileDemoScoringPrediction',
        demo_sales_prediction_model: 'analysePageSidebarDropdownTileDemoSalesPredictionModel',
        demo_potential_within_chain: 'analysePageSidebarDropdownTileDemoPotentialWithinChain',
        demo_store_chain_impact: 'analysePageSidebarDropdownTileDemoStoreChainImpact',
      },
    },
    catchment: {
      bar: 'analysePageCatchmentBar',
      custom: {
        button: 'analysePageCatchmentCustomButton',
        bar: 'analysePageCatchmentCustomBar',
        slider: 'analysePageCatchmentCustomSlider',
        showOnMapButton: 'analysePageCatchmentCustomShowOnMapButton',
        markOnMapButton: 'analysePageCatchmentCustomMarkOnMapButton',
        approveButton: 'analysePageCatchmentCustomApproveButton',
      },
    },
    selector: {
      dateRange: 'analysePageSelectorDateRange',
    },
    tile: {
      overview: {
        wrapper: 'analysePageTileOverviewWrapper',
      },
      traffic: {
        averageUniqueTraffic: 'analysePageTileTrafficAverageUniqueTraffic',
        addMapViewWrapper: 'analysePageTileTrafficAddMapViewWrapper',
      },
      footer: {
        approveButton: 'analysePageTileFooterApproveButton',
      },
    },
  },
} as const
